import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { ConfirmModal } from "@/components/modal/ConfirmModal";
import { NeedInfoModal } from "@/components/modal/NeedInfoModal";
import { ConfirmFertSlotRequestOrderModal } from "@/components/modal/ConfirmFertSlotRequestOrderModal";
import { RejectRequestModal } from "@/components/modal/RejectRequestModal";
import { RequestTypes } from "@/constants/enums";
import { SuppliesOrderStatus, SuppliesRequestStatus } from "@/constants/entity-status";
import { useRoles } from "@/hooks/useRoles";
import { Flex, Text, useDisclosure } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  FertSlotRequestDetail,
  FertSlotRequestOrder,
  FertSlotRequestProduct,
} from "./schema/fert-slot-request-detail.schema";
import { FertSlotRequestActions } from "@/components/common/section-components/requests/fert-slot-request/FertSlotRequestActions";
import { FertSlotRequestMainData } from "@/components/common/section-components/requests/fert-slot-request/FertSlotRequestMainData";
import { FertSlotRequestProductsTable } from "@/components/common/section-components/requests/fert-slot-request/FertSlotRequestProductsTable";
import {
  FertSlotRequestOrdersTable,
  FertSlotRequestOrdersTableItem,
} from "@/components/common/section-components/requests/fert-slot-request/FertSlotRequestOrdersTable";
import { FertSlotRequestMessagesTable } from "@/components/common/section-components/requests/fert-slot-request/FertSlotRequestMessagesTable";
import { toaster } from "@/components/ui/toaster";

import {
  confirmOrderHandler,
  fetchDetailHandler,
  fetchOrdersHandler,
  fetchStatusOptionsHandler,
  getStatusIdByNameHandler,
  handleCreateOrderHandler,
  handleEditOrdersHandler,
  rejectHandler,
  sendInfoHandler,
} from "./schema/handler-functions";
import { AppRoutes } from "@/constants/routes";
import { getChoferes, getEquipos } from "@/services/aux-entities.service";

const DetailLegend = ({ title }: { title: string }) => {
  return (
    <Flex alignItems="center" gap="30px">
      <Text fontWeight={700}>{title}</Text>
    </Flex>
  );
};

export const FertSlotRequestDetailPage = () => {
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchPermissions } = useRoles();

  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingSendInfo, setLoadingSendInfo] = useState(false);
  const [loadingReject, setLoadingReject] = useState(false);
  const [loadingConfirmOrderId, setLoadingConfirmOrderId] = useState<
    number | null
  >(null);

  const [detail, setDetail] = useState<FertSlotRequestDetail | null>(null);
  const [orders, setOrders] = useState<FertSlotRequestOrder[] | undefined>(
    undefined,
  );

  const { open: openInfoModal, setOpen: setOpenInfoModal } = useDisclosure();
  const { open: openRejectModal, setOpen: setOpenRejectModal } =
    useDisclosure();

  const { open: openConfirmOrderModal, setOpen: setOpenConfirmOrderModal } =
    useDisclosure();
  const [selectedConfirmOrderId, setSelectedConfirmOrderId] = useState<
    number | null
  >(null);

  const [driverOptions, setDriverOptions] = useState<
    { label: string; value: number }[]
  >([]);
  const [equipmentOptions, setEquipmentOptions] = useState<
    { label: string; value: number }[]
  >([]);
  const [loadingAuxOptions, setLoadingAuxOptions] = useState<boolean>(false);

  const [openConfirmLastPending, setOpenConfirmLastPending] =
    useState<boolean>(false);
  const [pendingConfirmPayload, setPendingConfirmPayload] = useState<{
    orderId: number;
    driverId: number;
    equipmentId: number;
    deliveryDate: string;
  } | null>(null);

  const handleBack = () => {  
     navigate(AppRoutes.FERT_SLOTS_REQ);
  };

  const fetchDetail = async (showLoad = true) => {
    await fetchDetailHandler({
      id,
      navigate,
      fetchPermissions,
      setLoadingDetail,
      setDetail,
      showLoad,
    });
  };

  const fetchOrders = async (showLoad = true) => {
    await fetchOrdersHandler({
      id,
      navigate,
      fetchPermissions,
      setLoadingOrders,
      setOrders,
      showLoad,
    });
  };

  useEffect(() => {
    if (id && isActive) {
      fetchDetail();
      fetchOrders();
    }
  }, [id, isActive]);

  useEffect(() => {
    if (!isActive) return;

    let cancelled = false;

    const fetchAuxOptions = async () => {
      try {
        setLoadingAuxOptions(true);
        const [drivers, equipments] = await Promise.all([
          getChoferes(),
          getEquipos(),
        ]);

        if (cancelled) return;

        setDriverOptions(
          (drivers ?? []).map((d) => ({
            label: d.nombreApellido,
            value: d.id,
          })),
        );

        setEquipmentOptions(
          (equipments ?? []).map((e) => ({
            label: e.nombre,
            value: e.id,
          })),
        );
      } catch {
        if (cancelled) return;
        setDriverOptions([]);
        setEquipmentOptions([]);
        toaster.create({
          type: "error",
          title: "No se pudieron cargar choferes/equipos.",
        });
      } finally {
        if (!cancelled) setLoadingAuxOptions(false);
      }
    };

    fetchAuxOptions();

    return () => {
      cancelled = true;
    };
  }, [isActive]);

  const [statusOptions, setStatusOptions] = useState<
    { nombre: string; id: number }[]
  >([]);

  const fetchStatusOptions = async () => {
    await fetchStatusOptionsHandler({
      setStatusOptions,
    });
  };

  useEffect(() => {
    if (isActive) {
      fetchStatusOptions();
    }
  }, [isActive]);

  const getStatusIdByName = (name: string, fallbackId?: number) => {
    return getStatusIdByNameHandler(name, statusOptions, fallbackId);
  };

  const productsTableItems =
    detail?.productos?.map((p: FertSlotRequestProduct) => ({
      ...p,
      cantidad: `${new Intl.NumberFormat("es-AR").format(p?.cantidad ?? 0)}`,
    })) ?? [];

  const ordersTableItems: FertSlotRequestOrdersTableItem[] = (orders ??
    []) as any;

  const handleSendInfo = async (event?: FieldValues) => {
    await sendInfoHandler({
      id,
      event,
      navigate,
      fetchPermissions,
      getStatusIdByName,
      setLoadingSendInfo,
      setOpenInfoModal,
      fetchDetail,
    });
  };

  const handleReject = async (event?: FieldValues) => {
    await rejectHandler({
      id,
      event,
      navigate,
      fetchPermissions,
      getStatusIdByName,
      setLoadingReject,
      setOpenRejectModal,
      fetchDetail,
    });
  };

  const handleCreateOrder = () => {
    handleCreateOrderHandler(navigate, id);
  };

  const handleEditOrders = () => {
    handleEditOrdersHandler(navigate, id);
  };

  const handleOpenConfirmOrderModal = (orderId: number) => {
    setSelectedConfirmOrderId(orderId);
    setOpenConfirmOrderModal(true);
  };

  const handleConfirmOrderSubmit = async (payload: {
    orderId: number;
    driverId: number;
    equipmentId: number;
    deliveryDate: string;
  }) => {
    const pendingOrders = (orders ?? []).filter(
      (o) => o?.estado === SuppliesOrderStatus.PENDING,
    );

    if (pendingOrders.length === 1) {
      setPendingConfirmPayload(payload);
      setOpenConfirmLastPending(true);
      return;
    }

    await confirmOrderHandler({
      orderId: payload.orderId,
      body: {
        fechaEntrega: payload.deliveryDate,
        idChofer: String(payload.driverId),
        idEquipo: Number(payload.equipmentId),
      },
      id,
      navigate,
      fetchPermissions,
      setLoadingConfirmOrderId,
      fetchOrders,
    });

    fetchDetail(false);
  };

  const handleAcceptConfirmLastPending = async () => {
    if (!pendingConfirmPayload) return;

    await confirmOrderHandler({
      orderId: pendingConfirmPayload.orderId,
      body: {
        fechaEntrega: pendingConfirmPayload.deliveryDate,
        idChofer: String(pendingConfirmPayload.driverId),
        idEquipo: Number(pendingConfirmPayload.equipmentId),
      },
      id,
      navigate,
      fetchPermissions,
      setLoadingConfirmOrderId,
      fetchOrders,
    });

    setOpenConfirmLastPending(false);
    setPendingConfirmPayload(null);
    fetchDetail(false);
  };

  const loading =
    loadingDetail ||
    loadingOrders ||
    loadingSendInfo ||
    loadingReject ||
    loadingConfirmOrderId !== null;

  const canCreateOrder =
    detail?.estado === SuppliesRequestStatus.PENDING_CONFIRMATION ||
    detail?.estado === SuppliesRequestStatus.NEEDS_INFO;

  const canSendInfo =
    detail?.estado === SuppliesRequestStatus.PENDING_CONFIRMATION ||
    detail?.estado === SuppliesRequestStatus.NEEDS_INFO;

  const canReject =
    detail?.estado !== SuppliesRequestStatus.ORDER_CONFIRMED &&
    detail?.estado !== SuppliesRequestStatus.DELIVERED &&
    detail?.estado !== SuppliesRequestStatus.PARTIAL_DELIVERY &&
    detail?.estado !== SuppliesRequestStatus.IN_TRANSIT &&
    detail?.estado !== SuppliesRequestStatus.PARTIALLY_CONFIRMED &&
    detail?.estado !== SuppliesRequestStatus.REJECTED &&
    detail?.estado !== SuppliesRequestStatus.CANCELLED;

  return (
    <Flex
      flexDir="column"
      paddingTop="20px"
      gap="12px"
      height="100%"
      minHeight={0}
    >
      <SectionHeader
        title="Solicitud de fertilizante"
        onBack={handleBack}
        backButton
        loading={loading}
      />

      {!loadingDetail && (
        <Flex
          flex="1"
          minHeight={0}
          overflowY="auto"
          flexDir="column"
          gap="12px"
        >
          <FertSlotRequestProductsTable
            items={productsTableItems}
            rightElement={
              <FertSlotRequestActions
                showReject={canReject}
                showNeedsInfo={canSendInfo}
                showCreateOrder={canCreateOrder}
                loadingSendInfo={loadingSendInfo}
                loadingReject={loadingReject}
                setOpenInfoModal={setOpenInfoModal}
                setOpenRejectModal={setOpenRejectModal}
                handleCreateOrder={handleCreateOrder}
              />
            }
          />

          <FertSlotRequestMainData
            fechaEntrega={detail?.fechaEntrega}
            titularCuenta={detail?.titularCuenta}
            destino={detail?.destino}
            kmDistancia={detail?.kmDistancia}
            contacto={detail?.contacto}
            estadoDescripcion={detail?.estado}
          />

          <DetailLegend title="Mensajes" />

          <Flex flexDir="column" flexShrink={0}>
            <FertSlotRequestMessagesTable messages={detail?.mensajes || []} />
          </Flex>

          {Boolean(orders?.length) && (
            <FertSlotRequestOrdersTable
              orders={ordersTableItems}
              loadingConfirmOrderId={loadingConfirmOrderId}
              onConfirmOrder={handleOpenConfirmOrderModal}
              showEditOrders={detail?.estado === SuppliesRequestStatus.ORDER_CREATED || detail?.estado === SuppliesRequestStatus.PARTIALLY_CONFIRMED}
              onEditOrders={handleEditOrders}
            />
          )}
        </Flex>
      )}

      {detail?.id && (
        <>
          <ConfirmModal
            openConfirm={openConfirmLastPending}
            setOpenConfirm={setOpenConfirmLastPending}
            handleAccept={handleAcceptConfirmLastPending}
            loadingAccept={
              pendingConfirmPayload
                ? loadingConfirmOrderId === pendingConfirmPayload.orderId
                : false
            }
            title="Confirmación de solicitud"
            subtitle="Si confirmas este pedido, ya no podrás volver a agregar o editar los pedidos"
          />

          {selectedConfirmOrderId !== null && (
            <ConfirmFertSlotRequestOrderModal
              open={openConfirmOrderModal}
              setOpen={(flag) => {
                setOpenConfirmOrderModal(flag);
                if (!flag) setSelectedConfirmOrderId(null);
              }}
              orderId={selectedConfirmOrderId}
              loadingConfirm={loadingConfirmOrderId === selectedConfirmOrderId}
              loadingDrivers={loadingAuxOptions}
              loadingEquipments={loadingAuxOptions}
              handleConfirmOrder={handleConfirmOrderSubmit}
              driverOptions={driverOptions}
              equipmentOptions={equipmentOptions}
            />
          )}
          <NeedInfoModal
            open={openInfoModal}
            setOpen={setOpenInfoModal}
            handleAccept={handleSendInfo}
            loadingAccept={loadingSendInfo}
            idTipoSolicitud={RequestTypes.CuposFertilizante}
          />
          <RejectRequestModal
            open={openRejectModal}
            setOpen={setOpenRejectModal}
            handleAccept={handleReject}
            loadingAccept={loadingReject}
            idTipoSolicitud={RequestTypes.CuposFertilizante}
            entityName="solicitud de fertilizante"
          />
        </>
      )}
    </Flex>
  );
};

